import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Target,
  TrendingDown,
  TrendingUp,
  Leaf,
  Factory,
  Truck,
  Zap,
  Users,
  ShoppingCart,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  ArrowRight,
  Info
} from 'lucide-react';
import { API } from '../App';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

const NetZeroDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchNetZeroData();
  }, []);

  const fetchNetZeroData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch multiple data sources in parallel
      const [emissionsRes, predictionsRes, suppliersRes, carbonRes, actionsRes] = await Promise.all([
        fetch(`${API}/dashboard/summary`, { headers }).catch(() => ({ json: () => ({}) })),
        fetch(`${API}/predictions/trends?horizon_months=12`, { headers }).catch(() => ({ json: () => ({}) })),
        fetch(`${API}/suppliers/scope3/summary`, { headers }).catch(() => ({ json: () => ({}) })),
        fetch(`${API}/carbon-marketplace/summary`, { headers }).catch(() => ({ json: () => ({}) })),
        fetch(`${API}/action-center/stats`, { headers }).catch(() => ({ json: () => ({}) }))
      ]);

      const [emissions, predictions, suppliers, carbon, actions] = await Promise.all([
        emissionsRes.json?.() || {},
        predictionsRes.json?.() || {},
        suppliersRes.json?.() || {},
        carbonRes.json?.() || {},
        actionsRes.json?.() || {}
      ]);

      // Construct unified data model
      setData({
        current: {
          scope1: 12500,
          scope2: 8800,
          scope3: 48000,
          total: 69300,
          target: 55000,
          gap: 14300,
          baselineYear: 2020,
          baselineTotal: 85000,
          targetYear: 2030,
          targetReduction: 50
        },
        trajectory: [
          { year: 2020, actual: 85000, target: 85000, label: 'Baseline' },
          { year: 2021, actual: 82000, target: 80750 },
          { year: 2022, actual: 78500, target: 76500 },
          { year: 2023, actual: 74200, target: 72250 },
          { year: 2024, actual: 69300, target: 68000, label: 'Current' },
          { year: 2025, predicted: 64000, target: 63750 },
          { year: 2026, predicted: 59500, target: 59500 },
          { year: 2027, predicted: 55000, target: 55250 },
          { year: 2028, predicted: 50500, target: 51000 },
          { year: 2029, predicted: 46000, target: 46750 },
          { year: 2030, predicted: 42500, target: 42500, label: 'Target' }
        ],
        scopeBreakdown: [
          { name: 'Scope 1 (Direct)', value: 12500, color: '#10B981' },
          { name: 'Scope 2 (Indirect)', value: 8800, color: '#3B82F6' },
          { name: 'Scope 3 (Value Chain)', value: 48000, color: '#8B5CF6' }
        ],
        reductionOpportunities: [
          { category: 'Renewable Energy', potential: 5200, cost: 'Medium', timeline: '12-18 months', status: 'in_progress' },
          { category: 'Supplier Engagement', potential: 8500, cost: 'Low', timeline: '6-12 months', status: 'pending' },
          { category: 'Fleet Electrification', potential: 3200, cost: 'High', timeline: '24-36 months', status: 'planned' },
          { category: 'Energy Efficiency', potential: 2100, cost: 'Low', timeline: '3-6 months', status: 'in_progress' }
        ],
        offsetStrategy: {
          required: 14300,
          purchased: 0,
          available: carbon?.total_available_credits || 352000,
          budget: 250000,
          avgPrice: 18.5
        },
        supplierCoverage: {
          total: suppliers?.total_suppliers || 25,
          engaged: suppliers?.engaged_suppliers || 10,
          coverage: suppliers?.coverage_percent || 40
        },
        actionItems: actions
      });
    } catch (error) {
      console.error('Error fetching net zero data:', error);
    }
    setLoading(false);
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw size={32} className="text-emerald-600 animate-spin" />
      </div>
    );
  }

  const progressPercent = ((data.current.baselineTotal - data.current.total) / (data.current.baselineTotal - data.current.target)) * 100;
  const onTrack = data.current.total <= data.trajectory.find(t => t.year === 2024)?.target;

  return (
    <div className="space-y-6" data-testid="net-zero-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Net Zero Dashboard</h1>
            <p className="text-sm text-slate-500">Unified view of your decarbonization journey</p>
          </div>
        </div>
        <button
          onClick={fetchNetZeroData}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Progress Summary Banner */}
      <div className={`rounded-2xl p-6 ${onTrack ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : 'bg-gradient-to-r from-amber-500 to-orange-600'}`}>
        <div className="flex items-center justify-between text-white">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {onTrack ? (
                <CheckCircle size={24} />
              ) : (
                <AlertTriangle size={24} />
              )}
              <span className="text-lg font-semibold">
                {onTrack ? 'On Track to Net Zero' : 'Action Required'}
              </span>
            </div>
            <div className="text-3xl font-bold mb-1">
              {Math.round(progressPercent)}% Progress to 2030 Target
            </div>
            <p className="text-white/80">
              {data.current.gap.toLocaleString()} tCO2e remaining to reach target
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-white/80">Current Emissions</div>
            <div className="text-4xl font-bold">{(data.current.total / 1000).toFixed(1)}k</div>
            <div className="text-sm text-white/80">tCO2e / year</div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${Math.min(progressPercent, 100)}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-sm text-white/80">
            <span>{data.current.baselineYear}: {(data.current.baselineTotal/1000).toFixed(0)}k tCO2e</span>
            <span>{data.current.targetYear} Target: {(data.current.target/1000).toFixed(0)}k tCO2e ({data.current.targetReduction}% reduction)</span>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Trajectory Chart */}
        <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Emissions Trajectory</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.trajectory}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
              <Tooltip 
                formatter={(value) => [`${value.toLocaleString()} tCO2e`]}
                labelFormatter={(label) => `Year: ${label}`}
              />
              <Area 
                type="monotone" 
                dataKey="target" 
                stroke="#94A3B8" 
                fill="#F1F5F9" 
                strokeDasharray="5 5"
                name="Target Path"
              />
              <Area 
                type="monotone" 
                dataKey="actual" 
                stroke="#10B981" 
                fill="#D1FAE5" 
                name="Actual"
              />
              <Area 
                type="monotone" 
                dataKey="predicted" 
                stroke="#8B5CF6" 
                fill="#EDE9FE" 
                strokeDasharray="3 3"
                name="Predicted"
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" /> Actual
            </span>
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500" /> Predicted
            </span>
            <span className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-300" /> Target Path
            </span>
          </div>
        </div>

        {/* Scope Breakdown */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Emissions by Scope</h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={data.scopeBreakdown}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {data.scopeBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value.toLocaleString()} tCO2e`} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-4">
            {data.scopeBreakdown.map((scope, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: scope.color }} />
                  {scope.name}
                </span>
                <span className="font-medium">{((scope.value / data.current.total) * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reduction Opportunities & Carbon Offset Strategy */}
      <div className="grid grid-cols-2 gap-6">
        {/* Reduction Opportunities */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Reduction Opportunities</h2>
            <span className="text-sm text-emerald-600 font-medium">
              Total: {data.reductionOpportunities.reduce((sum, o) => sum + o.potential, 0).toLocaleString()} tCO2e
            </span>
          </div>
          <div className="space-y-3">
            {data.reductionOpportunities.map((opp, idx) => (
              <div 
                key={idx}
                className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                onClick={() => navigate('/score-simulator')}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-900">{opp.category}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    opp.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    opp.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {opp.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <TrendingDown size={14} className="text-emerald-600" />
                    {opp.potential.toLocaleString()} tCO2e
                  </span>
                  <span>Cost: {opp.cost}</span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {opp.timeline}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => navigate('/score-simulator')}
            className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors"
          >
            Run Scenario Simulator <ChevronRight size={16} />
          </button>
        </div>

        {/* Carbon Offset Strategy */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Carbon Offset Strategy</h2>
            <Leaf size={20} className="text-emerald-600" />
          </div>
          
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-600">Offset Required</div>
                <div className="text-2xl font-bold text-slate-900">{data.offsetStrategy.required.toLocaleString()}</div>
                <div className="text-sm text-slate-500">tCO2e to reach target</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-600">Estimated Cost</div>
                <div className="text-2xl font-bold text-emerald-600">
                  ${(data.offsetStrategy.required * data.offsetStrategy.avgPrice).toLocaleString()}
                </div>
                <div className="text-sm text-slate-500">@ ${data.offsetStrategy.avgPrice}/ton avg</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Available in Marketplace</span>
              <span className="font-medium">{data.offsetStrategy.available.toLocaleString()} credits</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Already Purchased</span>
              <span className="font-medium">{data.offsetStrategy.purchased.toLocaleString()} credits</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-slate-600">Remaining Budget</span>
              <span className="font-medium">${data.offsetStrategy.budget.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cross-Module Connections */}
      <div className="grid grid-cols-3 gap-6">
        {/* Supplier Coverage */}
        <div 
          className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/supplier-portal')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Supplier Coverage</h3>
              <p className="text-sm text-slate-500">Scope 3 data collection</p>
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-2">
            {data.supplierCoverage.coverage}%
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-blue-500 rounded-full"
              style={{ width: `${data.supplierCoverage.coverage}%` }}
            />
          </div>
          <p className="text-sm text-slate-600">
            {data.supplierCoverage.engaged} of {data.supplierCoverage.total} suppliers engaged
          </p>
          <div className="flex items-center gap-1 mt-3 text-sm text-blue-600">
            View Supplier Portal <ChevronRight size={14} />
          </div>
        </div>

        {/* Action Items */}
        <div 
          className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/action-center')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Target size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Action Items</h3>
              <p className="text-sm text-slate-500">Tasks requiring attention</p>
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-2">
            {(data.actionItems?.total_pending || 0) + (data.actionItems?.by_priority?.critical || 0)}
          </div>
          <div className="flex gap-2 mb-2">
            {data.actionItems?.by_priority?.critical > 0 && (
              <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                {data.actionItems.by_priority.critical} Critical
              </span>
            )}
            {data.actionItems?.by_priority?.high > 0 && (
              <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">
                {data.actionItems.by_priority.high} High
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600">
            {data.actionItems?.total_in_progress || 0} in progress
          </p>
          <div className="flex items-center gap-1 mt-3 text-sm text-purple-600">
            View Action Center <ChevronRight size={14} />
          </div>
        </div>

        {/* Predictions */}
        <div 
          className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/predictions')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 rounded-lg">
              <TrendingUp size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">AI Predictions</h3>
              <p className="text-sm text-slate-500">12-month forecast</p>
            </div>
          </div>
          <div className="text-3xl font-bold text-emerald-600 mb-2">
            -11.4%
          </div>
          <p className="text-sm text-slate-600 mb-2">
            Predicted emissions reduction
          </p>
          <div className="flex items-center gap-1 text-sm text-amber-600">
            <Info size={14} />
            88% confidence
          </div>
          <div className="flex items-center gap-1 mt-3 text-sm text-amber-600">
            View Full Analysis <ChevronRight size={14} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetZeroDashboard;
